import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

export type OrgType = 'sfo' | 'mfo';

export interface Org {
  id: string;
  name: string;
  org_type: OrgType;
  base_ccy: string;
  tz: string;
  created_at: string;
  jurisdiction?: string;
  reporting_ccy?: string;
  date_format?: string;
  number_format?: string;
  reg_framework?: string;
  fiscal_year_end?: string;
  logo_url?: string | null;
  primary_color?: string | null;
  accent_color?: string | null;
  font_family?: string | null;
}

export interface Family {
  id: string;
  org_id: string;
  name: string;
  meta: Record<string, unknown>;
  created_at: string;
  onboarding_status?: 'draft' | 'kyc_pending' | 'data_pending' | 'review' | 'active';
  portal_access_enabled?: boolean;
}

export interface OrgRole {
  user_id: string;
  org_id: string;
  role: 'OrgAdmin' | 'OrgManager' | 'OrgAnalyst' | 'OrgViewer';
}

export interface FamilyRole {
  user_id: string;
  family_id: string;
  role: 'FamilyAdmin' | 'FamilyManager' | 'FamilyAnalyst' | 'FamilyViewer';
}

export function useOrg() {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || null);
      if (user) {
        Sentry.setUser({ id: user.id, email: user.email ?? undefined });
      } else {
        Sentry.setUser(null);
      }
    });
  }, []);

  const { data: orgRoles = [], isLoading: loadingRoles } = useQuery({
    queryKey: ['org-roles', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('users_org_roles')
        .select('*')
        .eq('user_id', userId);
      if (error) throw error;
      return data as OrgRole[];
    },
    enabled: !!userId,
  });

  const { data: orgs = [], isLoading: loadingOrgs } = useQuery({
    queryKey: ['orgs', orgRoles],
    queryFn: async () => {
      if (orgRoles.length === 0) return [];
      const orgIds = orgRoles.map(r => r.org_id);
      const { data, error } = await supabase
        .from('orgs')
        .select('*')
        .in('id', orgIds);
      if (error) throw error;
      return data as Org[];
    },
    enabled: orgRoles.length > 0,
  });

  const currentOrg = orgs[0] || null;
  const currentOrgRole = orgRoles.find(r => r.org_id === currentOrg?.id);

  useEffect(() => {
    if (currentOrg) {
      Sentry.setTag("org_id", currentOrg.id);
    }
  }, [currentOrg]);

  const { data: families = [], isLoading: loadingFamilies } = useQuery({
    queryKey: ['families', currentOrg?.id],
    queryFn: async () => {
      if (!currentOrg) return [];
      const { data, error } = await supabase
        .from('families')
        .select('*')
        .eq('org_id', currentOrg.id);
      if (error) throw error;
      return data as Family[];
    },
    enabled: !!currentOrg,
  });

  const isSFO = currentOrg?.org_type === 'sfo';
  const isMFO = currentOrg?.org_type === 'mfo';
  const isOrgAdmin = currentOrgRole?.role === 'OrgAdmin';
  const defaultFamily = isSFO ? families[0] : null;

  return {
    currentOrg,
    currentOrgRole,
    families,
    isSFO,
    isMFO,
    isOrgAdmin,
    defaultFamily,
    isLoading: userId === null || loadingRoles || (orgRoles.length > 0 && loadingOrgs) || (!!currentOrg && loadingFamilies),
  };
}
